import { useState } from 'react';
import { KeyRound, Pencil, Plus, Smartphone, Trash2, TriangleAlert, Users } from 'lucide-react';
import {
  formatUzPhone,
  type StoreroomWorkerDto,
  type WorkerPinResultDto,
} from '@eombor/contracts';
import { Badge, Button, Card, ConfirmDialog, EmptyState, Skeleton } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { useMyStorerooms } from '../../subdivision/model/subdivision.queries.js';
import { useDeleteWorker, useResetPin, useWorkers } from '../model/storekeeping.queries.js';
import { initialsOf } from './UsageHistoryView.js';
import { PinRevealDialog, WorkerDialog } from './WorkerDialog.js';

const pad = (n: number): string => String(n).padStart(2, '0');
const moment = (iso: string): string => {
  const d = new Date(iso);
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * OMBOR XODIMLARI — mas'ulning boshqaruv paneli.
 *
 * Faqat o'zi mas'ul bo'lgan omborlar ko'rinadi: boshqasiga xodim biriktirib
 * bo'lmaydi va tugma ham chizilmaydi.
 *
 * «Ulangan telefonlar» bo'limi YO'Q va bu ataylab: telefon endi omborga
 * emas, xodimning O'ZIGA tegishli. U o'z raqami va PIN i bilan kiradi,
 * ruxsat esa quyidagi kartochkadagi omborlardan kelib chiqadi.
 */
export const TeamView = () => {
  const rooms = useMyStorerooms();
  const workers = useWorkers();
  const resetPin = useResetPin();
  const remove = useDeleteWorker();
  const [dialog, setDialog] = useState<{ worker: StoreroomWorkerDto | null } | null>(null);
  const [removing, setRemoving] = useState<StoreroomWorkerDto | null>(null);
  const [pin, setPin] = useState<WorkerPinResultDto | null>(null);
  const managed = (rooms.data ?? []).filter((room) => room.canManage && room.isActive);

  if (rooms.isPending || workers.isPending) return <Skeleton className="h-64 w-full" />;
  if (workers.isError) {
    return (
      <Card>
        <EmptyState icon={<TriangleAlert size={20} aria-hidden />} title={t('myStock.loadError')} description={workers.error.message} />
      </Card>
    );
  }
  if (managed.length === 0) {
    return (
      <Card>
        <EmptyState icon={<Users size={20} aria-hidden />} title={t('team.manageOnly')} />
      </Card>
    );
  }

  const roomName = (id: string): string => rooms.data?.find((room) => room.id === id)?.name ?? `#${id}`;

  return (
    <div className="flex flex-col gap-6">
      <section aria-label={t('team.workers')} className="flex flex-col gap-3">
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div className="flex flex-col gap-0.5">
            <h2 className="text-base font-semibold text-text">{t('team.workers')}</h2>
            <p className="text-sm text-text-muted">{t('team.workersHint')}</p>
          </div>
          <Button onClick={() => setDialog({ worker: null })}>
            <Plus size={16} aria-hidden />
            {t('team.addWorker')}
          </Button>
        </header>

        {workers.data.length === 0 ? (
          <Card>
            <EmptyState icon={<Users size={20} aria-hidden />} title={t('team.noWorkers')} />
          </Card>
        ) : (
          <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {workers.data.map((worker) => (
              <li
                key={worker.id}
                className="flex flex-col gap-3 rounded-[var(--radius-lg)] border border-border bg-surface p-4 shadow-[var(--shadow-1)]"
              >
                <div className="flex items-start gap-3">
                  <span
                    aria-hidden
                    className="flex size-11 shrink-0 items-center justify-center rounded-full bg-accent text-sm font-semibold text-[var(--color-accent-contrast)]"
                  >
                    {initialsOf(worker.fullName)}
                  </span>
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="truncate text-sm font-semibold text-text">{worker.fullName}</span>
                    <span className="truncate text-xs text-text-muted">{worker.position ?? '—'}</span>
                    {/* RAQAM — ilovaga kirish kaliti, shuning uchun ism bilan
                        bir qatorda turadi va yashirilmaydi: mas'ul kimga PIN
                        berganini shu yerdan taniydi. */}
                    <span className="tabular flex items-center gap-1 truncate text-xs text-text-muted">
                      <Smartphone size={12} aria-hidden />
                      {worker.phone === null ? t('team.noPhone') : formatUzPhone(worker.phone)}
                    </span>
                  </div>
                  {worker.isActive ? null : <Badge tone="neutral">{t('team.inactive')}</Badge>}
                  {worker.lockedUntil === null ? null : <Badge tone="danger">{t('team.locked')}</Badge>}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {worker.storeroomIds.map((id) => (
                    <Badge key={id} tone="info">
                      {roomName(id)}
                    </Badge>
                  ))}
                </div>
                <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-2.5">
                  <span className="tabular text-xs text-text-muted">
                    {worker.lastUsedAt === null ? t('team.neverUsed') : t('team.lastUsed', { date: moment(worker.lastUsedAt) })}
                  </span>
                  <span className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      loading={resetPin.isPending && resetPin.variables === worker.id}
                      onClick={() => resetPin.mutate(worker.id, { onSuccess: setPin })}
                    >
                      <KeyRound size={14} aria-hidden />
                      {t('team.resetPin')}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setDialog({ worker })}>
                      <Pencil size={14} aria-hidden />
                      {t('team.edit')}
                    </Button>
                    {/* O'CHIRISH faqat xatoni tuzatish uchun: tugma o'zi
                        qo'shgan va hali hech narsa olmagan xodimdagina
                        chiziladi. Shartni server hisoblaydi (`canDelete`) —
                        ko'ringan tugma ishlamay qolmasin. */}
                    {worker.canDelete ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={t('team.deleteOne', { name: worker.fullName })}
                        onClick={() => setRemoving(worker)}
                      >
                        <Trash2 size={14} aria-hidden />
                        {t('team.delete')}
                      </Button>
                    ) : null}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {dialog === null ? null : (
        <WorkerDialog
          worker={dialog.worker}
          rooms={managed}
          onClose={() => setDialog(null)}
          onPin={(result) => {
            setDialog(null);
            setPin(result);
          }}
        />
      )}
      <ConfirmDialog
        open={removing !== null}
        onClose={() => {
          setRemoving(null);
          remove.reset();
        }}
        onConfirm={() => {
          if (removing !== null) {
            remove.mutate(removing.id, { onSuccess: () => setRemoving(null) });
          }
        }}
        title={t('team.deleteTitle')}
        description={
          removing === null ? null : (
            <span className="flex flex-col items-center gap-1">
              <span className="text-sm font-medium text-text">{removing.fullName}</span>
              <span>{t('team.deleteHint')}</span>
            </span>
          )
        }
        confirmLabel={t('team.delete')}
        cancelLabel={t('team.cancel')}
        tone="danger"
        loading={remove.isPending}
        {...(remove.error === null ? {} : { error: remove.error.message })}
      />
      {pin === null ? null : <PinRevealDialog result={pin} onClose={() => setPin(null)} />}
    </div>
  );
};
